import type { CatalogueDto, ConversationDto, GroupDto } from '@dbrain/shared';
import { Button, ConfirmDialog, Icons, Menu, MenuItem, MenuSeparator, Select, TextInput } from '@dbrain/ui';
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation, useNavigate } from 'react-router';
import { api } from '../../api/client.js';
import { keys, useActivity, useCatalogue, useGroups, useSearch } from '../../api/hooks.js';
import type { Experience } from '../../experience.js';
import { historyGroup, type HistoryGroup } from '@dbrain/app-core/when';
import { ErrorAlert } from '../ErrorAlert.js';
import { ProfileIcon, statusOf, StatusDot, useTimes, type ConversationStatus } from '../status.js';

/**
 * Conversation history (QW-201…QW-204, SA-008, D-20, D-33). The left menu
 * shows the recent ones by day — Bugün, Dün, Son 7 gün — each with its
 * kind's icon and a dot when it waits for the user or runs
 * (docs/design/mockups/CalismaAlanlari.dc.html); "Tümünü gör" opens the
 * whole history with the archive, the trash and the groups. Rename, pin,
 * group, archive and the trash are in each row's menu.
 */
export type View = 'active' | 'archived' | 'trash';
export type Row = ConversationDto & { workspaceName: string };

export function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/** Highlights the «matched» part of a search snippet. */
function Snippet({ text }: { text: string }) {
  const parts = text.split(/(«[^»]*»)/g);
  return (
    <span className="session-item__snippet">
      {parts.map((part, index) => (part.startsWith('«') ? <mark key={index}>{part.slice(1, -1)}</mark> : <span key={index}>{part}</span>))}
    </span>
  );
}

function SessionMenu({ row, view, groups, onChanged, onRename }: { row: Row; view: View; groups: GroupDto[]; onChanged: () => void; onRename: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [purging, setPurging] = useState(false);
  const patch = useMutation({ mutationFn: (body: Record<string, unknown>) => api('PATCH', `/api/conversations/${row.id}`, body), onSuccess: onChanged });
  const remove = useMutation({ mutationFn: () => api('DELETE', `/api/conversations/${row.id}`), onSuccess: onChanged });
  const restore = useMutation({ mutationFn: () => api('POST', `/api/conversations/${row.id}/restore`), onSuccess: onChanged });
  const purge = useMutation({
    mutationFn: () => api('POST', `/api/conversations/${row.id}/purge`),
    onSuccess: () => {
      setPurging(false);
      onChanged();
    },
  });
  const error = patch.error ?? remove.error ?? restore.error;
  return (
    <>
      <Menu
        className="session-item__menu"
        align="right"
        label={t('sessions.actions')}
        triggerClassName="ui-icon-button ui-icon-button--sm"
        trigger={<Icons.more size={16} />}
        data-testid="session-menu"
      >
        <ErrorAlert error={error} />
        {view === 'trash' ? (
          <>
            <MenuItem icon={<Icons.restore />} onClick={() => restore.mutate()} disabled={restore.isPending} data-testid="session-restore">
              {t('sessions.restore')}
            </MenuItem>
            <MenuItem danger icon={<Icons.trash />} onClick={() => setPurging(true)} data-testid="session-purge">
              {t('sessions.purge')}
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem icon={<Icons.edit />} onClick={onRename} data-testid="session-rename">
              {t('common.rename')}
            </MenuItem>
            {view === 'active' ? (
              <MenuItem icon={row.pinned ? <Icons.unpin /> : <Icons.pin />} onClick={() => patch.mutate({ pinned: !row.pinned })} data-testid="session-pin">
                {row.pinned ? t('sessions.unpin') : t('sessions.pin')}
              </MenuItem>
            ) : null}
            <label className="menu-field">
              <span>{t('sessions.group')}</span>
              <Select size="sm" value={row.groupId ?? ''} onChange={(e) => patch.mutate({ groupId: e.target.value || null })} data-testid="session-group">
                <option value="">{t('sessions.noGroup')}</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </Select>
            </label>
            <MenuItem icon={view === 'archived' ? <Icons.unarchive /> : <Icons.archive />} onClick={() => patch.mutate({ archived: view !== 'archived' })} data-testid="session-archive">
              {view === 'archived' ? t('sessions.unarchive') : t('sessions.archive')}
            </MenuItem>
            <MenuSeparator />
            <MenuItem
              danger
              icon={<Icons.trash />}
              disabled={remove.isPending}
              // leaving the page only when it shows the conversation that went to the trash
              onClick={() => remove.mutate(undefined, { onSuccess: () => location.pathname === `/c/${row.id}` && void navigate('/') })}
              data-testid="session-delete"
            >
              {t('sessions.toTrash')}
            </MenuItem>
          </>
        )}
      </Menu>
      {purging ? (
        <ConfirmDialog
          title={t('sessions.purgeTitle')}
          confirmLabel={t('sessions.purge')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          busy={purge.isPending}
          error={<ErrorAlert error={purge.error} />}
          onConfirm={() => purge.mutate()}
          onClose={() => setPurging(false)}
        >
          {t('sessions.purgeConfirm')}
        </ConfirmDialog>
      ) : null}
    </>
  );
}

function RenameRow({ row, onDone }: { row: Row; onDone: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(row.title ?? '');
  const patch = useMutation({
    mutationFn: (value: string) => api('PATCH', `/api/conversations/${row.id}`, { title: value }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.catalogueAll });
      void queryClient.invalidateQueries({ queryKey: keys.conversation(row.id) });
      onDone();
    },
  });
  return (
    <form
      className="session-item__rename"
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim()) patch.mutate(title.trim());
      }}
    >
      <TextInput
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => e.key === 'Escape' && onDone()}
        aria-label={t('common.rename')}
        autoFocus
        data-testid="session-rename-input"
      />
      <ErrorAlert error={patch.error} />
    </form>
  );
}

/**
 * A row: the kind's icon, the title, a dot when it waits or runs, and its
 * menu. On the history page it also says where it is and when.
 */
export function SessionItem({
  row,
  view,
  groups,
  onChanged,
  snippet,
  onNavigate,
  status,
  wide = false,
}: {
  row: Row;
  view: View;
  groups: GroupDto[];
  onChanged: () => void;
  snippet?: string;
  onNavigate?: () => void;
  status?: ConversationStatus | null;
  /** the history page: workspace and time on the row */
  wide?: boolean;
}) {
  const { t } = useTranslation();
  const times = useTimes();
  const [renaming, setRenaming] = useState(false);
  const group = groups.find((g) => g.id === row.groupId);
  const title = row.title ?? t('conversation.untitled');
  if (renaming) {
    return (
      <li className="session-item" data-testid="session-item" data-id={row.id}>
        <RenameRow row={row} onDone={() => setRenaming(false)} />
      </li>
    );
  }
  const lead = (
    <span className="session-item__lead">
      <ProfileIcon profile={row.profile} size={15} />
    </span>
  );
  const marks = (
    <>
      {row.pinned ? (
        <span className="session-item__mark" title={t('sessions.pinned')}>
          <Icons.pin size={13} />
          <span className="ui-visually-hidden">{t('sessions.pinned')}</span>
        </span>
      ) : null}
      {group ? (
        <span className="session-item__mark" title={group.name}>
          <span className={`group-dot group-dot--${group.color}`} aria-hidden="true" />
          <span className="ui-visually-hidden">{group.name}</span>
        </span>
      ) : null}
      <StatusDot status={status ?? null} />
    </>
  );
  const text = (
    <span className="session-item__text">
      <span className="session-item__title">{title}</span>
      {snippet ? <Snippet text={snippet} /> : null}
      {wide && !snippet ? <span className="session-item__meta">{row.workspaceName}</span> : null}
    </span>
  );
  return (
    <li className={['session-item', snippet ? 'session-item--hit' : '', wide ? 'session-item--wide' : ''].join(' ').trim()} data-testid="session-item" data-id={row.id}>
      {view === 'trash' ? (
        <span className="session-item__link session-item__link--disabled" title={row.workspaceName}>
          {lead}
          {text}
          {wide ? <span className="session-item__time">{times.list(row.deletedAt ?? row.lastActivityAt)}</span> : null}
        </span>
      ) : (
        <NavLink to={`/c/${row.id}`} className="session-item__link" title={`${title} · ${row.workspaceName}`} onClick={onNavigate}>
          {lead}
          {text}
          {marks}
          {wide ? <span className="session-item__time">{times.list(row.lastActivityAt)}</span> : null}
        </NavLink>
      )}
      <SessionMenu row={row} view={view} groups={groups} onChanged={onChanged} onRename={() => setRenaming(true)} />
    </li>
  );
}

export function GroupBar({ groups, groupId, onSelect }: { groups: GroupDto[]; groupId: string | null; onSelect: (id: string | null) => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: (id: string) => api('DELETE', `/api/conversation-groups/${id}`),
    onSuccess: () => {
      onSelect(null);
      void queryClient.invalidateQueries({ queryKey: keys.groups });
      void queryClient.invalidateQueries({ queryKey: keys.catalogueAll });
    },
  });
  return (
    <div className="group-bar" data-testid="group-bar">
      <button type="button" className={`group-chip ${groupId === null ? 'group-chip--active' : ''}`} aria-pressed={groupId === null} onClick={() => onSelect(null)}>
        {t('sessions.allGroups')}
      </button>
      {groups.map((group) => (
        <span key={group.id} className={`group-chip group-chip--${group.color} ${groupId === group.id ? 'group-chip--active' : ''}`}>
          <button type="button" aria-pressed={groupId === group.id} onClick={() => onSelect(group.id)}>
            <span className={`group-dot group-dot--${group.color}`} aria-hidden="true" />
            {group.name}
          </button>
          {groupId === group.id ? (
            <button type="button" aria-label={t('sessions.deleteGroup')} title={t('sessions.deleteGroup')} onClick={() => remove.mutate(group.id)}>
              <Icons.close size={14} />
            </button>
          ) : null}
        </span>
      ))}
    </div>
  );
}

export function NewGroupForm({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: (value: string) => api<GroupDto>('POST', '/api/conversation-groups', { name: value }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.groups });
      onDone();
    },
  });
  return (
    <form
      className="group-bar__form"
      onSubmit={(e) => {
        e.preventDefault();
        if (name.trim()) create.mutate(name.trim());
      }}
    >
      <TextInput value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Escape' && onDone()} aria-label={t('sessions.newGroup')} placeholder={t('sessions.newGroup')} autoFocus data-testid="group-name" />
      <Button size="sm" type="submit" busy={create.isPending} disabled={!name.trim()}>
        {t('common.create')}
      </Button>
      <ErrorAlert error={create.error} />
    </form>
  );
}

const GROUP_ORDER: HistoryGroup[] = ['today', 'yesterday', 'week', 'month', 'older'];

/** Rows under "Bugün", "Dün", "Son 7 gün"… in order of last activity. */
export function byDay(rows: Row[], now = new Date()): { group: HistoryGroup; rows: Row[] }[] {
  const buckets = new Map<HistoryGroup, Row[]>();
  for (const row of rows) {
    const group = historyGroup(row.lastActivityAt, now);
    buckets.set(group, [...(buckets.get(group) ?? []), row]);
  }
  return GROUP_ORDER.filter((group) => buckets.has(group)).map((group) => ({ group, rows: buckets.get(group) ?? [] }));
}

/** The history in the left menu: pinned ones, then by day; a search in place; the rest on the history page. */
export function SidebarHistory({ experience, onNavigate }: { experience: Experience; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');
  const q = useDebounced(query.trim(), 250);
  const catalogue = useCatalogue(experience, 'active', null);
  const search = useSearch(q, experience);
  const groups = useGroups();
  const activity = useActivity();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: keys.catalogueAll });
    void queryClient.invalidateQueries({ queryKey: ['search'] });
  };
  const pages = (catalogue.data as InfiniteData<CatalogueDto> | undefined)?.pages ?? [];
  const pinned = pages[0]?.pinned ?? [];
  const items = pages[0]?.items ?? [];
  const groupList = groups.data ?? [];
  const row = (item: Row, snippet?: string) => (
    <SessionItem key={item.id} row={item} view="active" groups={groupList} onChanged={refresh} snippet={snippet} onNavigate={onNavigate} status={statusOf(activity.data, item.id)} />
  );
  const closeSearch = () => {
    setSearching(false);
    setQuery('');
  };

  return (
    <section className="sidebar__group history" aria-labelledby="sidebar-history" data-testid="session-sidebar">
      <div className="sidebar__heading">
        <h2 id="sidebar-history">{t('shell.history')}</h2>
        <button
          type="button"
          className="ui-icon-button ui-icon-button--sm"
          aria-label={searching ? t('sessions.closeSearch') : t('sessions.search')}
          title={searching ? t('sessions.closeSearch') : t('sessions.search')}
          aria-expanded={searching}
          onClick={() => (searching ? closeSearch() : setSearching(true))}
          data-testid="history-search-toggle"
        >
          {searching ? <Icons.close size={15} /> : <Icons.search size={15} />}
        </button>
      </div>
      {searching ? (
        <label className="history__search">
          <Icons.search size={16} />
          <span className="ui-visually-hidden">{t('sessions.search')}</span>
          <input
            type="search"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Escape' && closeSearch()}
            placeholder={t('sessions.search')}
            data-testid="session-search"
          />
        </label>
      ) : null}
      {searching && q.length >= 2 ? (
        <ul className="session-list" data-testid="search-results">
          {search.isPending ? <li className="sidebar__empty">{t('common.loading')}</li> : null}
          {search.data?.length === 0 ? <li className="sidebar__empty">{t('sessions.noResults')}</li> : null}
          {search.data?.map((hit) => row(hit.conversation, hit.snippet))}
        </ul>
      ) : (
        <>
          <ErrorAlert error={catalogue.error} />
          {pinned.length > 0 ? (
            <>
              <p className="history__day">{t('sessions.pinnedTitle')}</p>
              <ul className="session-list" aria-label={t('sessions.pinnedTitle')} data-testid="pinned-list">
                {pinned.map((item) => row(item))}
              </ul>
            </>
          ) : null}
          <div data-testid="conversation-list">
            {byDay(items).map(({ group, rows }) => (
              <div key={group}>
                <p className="history__day" id={`history-${group}`}>
                  {t(`sessions.day.${group}`)}
                </p>
                <ul className="session-list" aria-labelledby={`history-${group}`}>
                  {rows.map((item) => row(item))}
                </ul>
              </div>
            ))}
          </div>
          {catalogue.isPending ? <p className="sidebar__empty">{t('common.loading')}</p> : null}
          {!catalogue.isPending && items.length === 0 && pinned.length === 0 ? <p className="sidebar__empty">{t('sessions.empty.active')}</p> : null}
          <Link to="/history" className="history__all" onClick={onNavigate} data-testid="history-all">
            {t('sessions.viewAll')}
          </Link>
        </>
      )}
    </section>
  );
}
